import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useIsDarkMode } from "../lib/utils";
import { useMediaQuery } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  TextField,
  Select,
  MenuItem,
  Typography,
} from "@mhome/ui";
import MTextField from "../common/text-field";
import { getCardColor, getCardColorOptions } from "../common/colors";

export default function CardContentEditDialog({ open, onClose, onSave, card }) {
  const [tempTitle, setTempTitle] = useState("");
  const [tempSubtitle, setTempSubtitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [selectedColor, setSelectedColor] = useState("default");
  const [titleWidget, setTitleWidget] = useState(null);
  const [saving, setSaving] = useState(false);
  const actualMode = useIsDarkMode();
  const isSmallScreen = useMediaQuery("@media (max-width:599px)");

  const handleCardData = (card) => {
    if (!card) {
      console.warn("Card data is null or undefined");
      return;
    }
    try {
      const tw = card.widgets?.find(
        (w) =>
          w?.skin === "default.title" || w?.skin === "default.automationtitle"
      );

      if (tw?.title) {
        setTempTitle(tw.title);
        setTempSubtitle(tw?.subtitle || "");
        setTitleWidget(tw);
      } else {
        setTempTitle("");
        setTempSubtitle("");
      }

      if (card.setting?.backgroundColor) {
        setSelectedColor(card.setting.backgroundColor);
      } else {
        setSelectedColor("default");
      }
    } catch (error) {
      console.error("Error processing card data:", error);
      setTempTitle("");
      setTempSubtitle("");
      setSelectedColor("default");
    }
  };

  useEffect(() => {
    try {
      if (card) {
        handleCardData(card);
      }
    } catch (error) {
      console.error("Error in useEffect:", error);
    }
  }, [card, open]);

  const handleTitleChange = (e) => {
    setTempTitle(e.target.value);
    setTitleError(!e.target.value.trim());
  };

  const handleSave = async () => {
    if (!tempTitle.trim()) {
      setTitleError(true);
      return;
    }

    setSaving(true);
    try {
      if (onSave) {
        const updates = [];
        if (tempTitle) {
          updates.push({
            ...titleWidget,
            title: tempTitle,
          });
        }

        if (updates.length > 0 || selectedColor) {
          await onSave({
            widget: updates,
            setting: {
              ...(card?.setting || {}),
              backgroundColor: selectedColor,
            },
          });
        }
      }
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="pops"
      PaperProps={{
        style: {
          width: isSmallScreen ? "calc(100% - 32px)" : "auto",
          maxWidth: isSmallScreen ? "calc(100% - 32px)" : "800px",
          minWidth: isSmallScreen ? "auto" : "300px",
          margin: isSmallScreen ? "16px" : "auto",
        },
      }}
    >
      <DialogTitle>Edit Card Content</DialogTitle>
      <DialogContent className="px-6 pt-4 pb-4">
        <TextField
          autoFocus
          disabled
          margin="dense"
          label="Title"
          fullWidth
          value={tempTitle}
          onChange={handleTitleChange}
          error={titleError}
          helperText={titleError ? "Title cannot be empty" : ""}
          className="mb-4"
        />

        <FormControl fullWidth margin="dense">
          <Typography variant="body2" className="mb-1 text-muted-foreground">
            Card Color
          </Typography>
          <Select
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
          >
            {getCardColorOptions(actualMode || "light").map((color) => {
              const themeMode = actualMode || "light";
              const backgroundColor =
                color.value === "default"
                  ? "hsl(var(--card))"
                  : getCardColor(color.value, themeMode);
              return (
                <MenuItem
                  key={color.value}
                  value={color.value}
                  style={{
                    color: themeMode === "dark" ? "#fff" : "#222",
                    backgroundColor: backgroundColor,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = backgroundColor;
                    e.currentTarget.style.opacity = "0.9";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = backgroundColor;
                    e.currentTarget.style.opacity = "1";
                  }}
                >
                  {color.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions className="justify-end gap-2">
        <Button onClick={onClose} variant="outlined" className="normal-case">
          Cancel
        </Button>
        <div className="relative">
          <Button
            variant="contained"
            onClick={handleSave}
            className="min-w-[80px] normal-case"
          >
            Save
            {saving && <CircularProgress size={20} className="ml-2" />}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
